/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {Provider} from 'react-redux';
import {Reducer} from 'redux';
import {initializeStore} from './store';

/**
 * Defines the `StoreProviderProps` prop type which accept a top-level `ReactNode`
 * (generally, a `Layout` Component), and the application's rootReducer store.
 *
 * @param children the current component which is to be rendered within the provider.
 * @param rootReducer The applications root reducer function containing all reducers.
 */
export type StoreProviderProps = {
  children: React.ReactNode;
  rootReducer: Reducer
};

/**
 * The `StoreProvider` provides the entry point for integrating with the global Redux
 * Store. This component should only be used directly by the root-level Application
 * Component.
 *
 * ```typescript
 *   <StoreProvider rootReducer={rootReducer}>
 *     <Layout {...layoutProps}>
 *       <Component {...pageProps} />
 *     </Layout>
 *   </StoreProvider>
 * ```
 *
 * @see {@link StoreProviderProps}
 */
export const StoreProvider: React.FC<StoreProviderProps> = ({children, rootReducer}) => {
  const store = useMemo(() => initializeStore(rootReducer), [rootReducer]);
  return (
    <Provider store={store}>
      {children}
    </Provider>
  );
};
export default StoreProvider;
